import { useEffect, useRef, useState } from 'react'
import { type Dispatch, type MutableRefObject, type SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import LogTool from '../../../logger/logTools'
import { createContact, getContactRepresentation } from '../../../utils/functions'
import { type Contact, type RichContact, type SelectOption } from '../../../utils/types'
import { useAllRichContacts } from '../../Contact/hooks/useContactQueries'
import { buildRichContactGrouping } from '../../Contact/utils/richContactHelpers'
import { useCreateContact } from '../../Contact/hooks/useContactMutations'

const log = new LogTool({ context: 'useContactHandler', enable: true, logLevel: 'warn' })

type AutomaticShareFormInput = {
  [inputKey: string]: { contact: SelectOption; customInvitationMessage: string }
}

/**
 * Custom hook that handles all contact-related logic for Sharepoint Drawers.
 * This hook manages:
 * - Loading contacts from the API
 * - Storing contacts in a ref
 * - Managing contact options for select inputs
 * - Managing contact form input state for ContactDrawer
 * - Creating new contacts and updating all related state
 *
 * @param isGuestUser - Whether the current user is a guest user (contacts are disabled for guests)
 * @param setAutomaticSharesFormInput - State setter for automatic shares form input (used to update when new contact is created)
 * @returns Object containing contactStore, contactOptions, contactFormInput state, newContactReceiver state, and handleAddContactOption function
 */
export function useContactHandler(
  isGuestUser: boolean,
  setAutomaticSharesFormInput: Dispatch<SetStateAction<AutomaticShareFormInput>>
): {
  contactStore: MutableRefObject<{ [relatedToName: string]: RichContact[] }>
  contactOptions: SelectOption[]
  contactFormInput: any
  setContactFormInput: Dispatch<SetStateAction<any>>
  newContactReceiver: string | undefined
  setNewContactReceiver: Dispatch<SetStateAction<string | undefined>>
  handleAddContactOption: (contactInput: any) => Promise<'SUCCESS' | 'ERROR'>
} {
  const contactStore = useRef<{ [relatedToName: string]: RichContact[] }>({})
  const [contactOptions, setContactOptions] = useState<SelectOption[]>([])
  const [contactFormInput, setContactFormInput] = useState<any>({})
  const [newContactReceiver, setNewContactReceiver] = useState<string | undefined>(undefined)
  const createContactMutation = useCreateContact()
  const { t } = useTranslation()

  // Fetch rich contacts using React Query (disabled for guest users)
  const {
    data: richContactsData,
    isError: richContactsError,
    error: richContactsErrorObj,
  } = useAllRichContacts({}, !isGuestUser)

  // Initialize contacts from API data
  useEffect(() => {
    if (richContactsError && richContactsErrorObj) {
      log.error('Error fetching rich contacts', richContactsErrorObj)
    }
  }, [richContactsError, richContactsErrorObj])

  useEffect(() => {
    if (!richContactsData) return
    const { store, options } = buildRichContactGrouping(richContactsData)
    contactStore.current = store
    setContactOptions(options)
  }, [richContactsData])

  const handleAddContactOption = async (contactInput: any): Promise<'SUCCESS' | 'ERROR'> => {
    log.info('Begin sending contact.')
    log.debug('Input ->', contactInput)

    try {
      // create new contact
      const response = await createContactMutation.mutateAsync(contactInput)
      // update contact options
      const newContact: Contact = createContact(response)
      if (contactStore.current['unrelated']) {
        contactStore.current['unrelated'].push(newContact as RichContact)
      } else {
        contactStore.current['unrelated'] = [newContact as RichContact]
      }
      setContactOptions(prev => [
        ...prev,
        {
          value: newContact.self,
          label: getContactRepresentation(newContact),
        },
      ])

      // update the select that was used to initiate the contact creation process
      if (newContactReceiver) {
        setAutomaticSharesFormInput(prev => {
          const updated = { ...prev }
          updated[newContactReceiver] = {
            contact: {
              value: newContact.self,
              label: getContactRepresentation(newContact),
            },
            customInvitationMessage: '',
          }
          return updated
        })
      }

      return 'SUCCESS'
    } catch (error) {
      log.error('Error creating contact option:', error)
      return 'ERROR'
    }
  }

  return {
    contactStore,
    contactOptions,
    contactFormInput,
    setContactFormInput,
    newContactReceiver,
    setNewContactReceiver,
    handleAddContactOption,
  }
}

